import { useEffect, useState } from "react";
import { getTags, createTag, updateTag, deleteTag } from "interfaces/tagProduct";
import TagForm from "components/tagProduct/tagForm";
import exp from "constants";
import { getMyInfo } from "interfaces/user";
import { toast } from "react-toastify";


const TagList: React.FC = () => {
  const [tags, setTags] = useState<any[]>([]);
  const [editingTag, setEditingTag] = useState<any | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [user, setUser] = useState<any>(null);

  // Charger les tags
  const fetchTags = async () => {
    const res = await getTags();
    if (res) setTags(res.data);
  };

  const initUser = async () => {
    const res = await getMyInfo();
    if (res) setUser(res.data);
  }

  useEffect(() => {
    fetchTags();
    initUser();
  }, []);

  // Sauvegarde depuis TagForm (création ou édition)
  const handleSave = async (tag: any) => {

    if (user?.isParent === false) {
      toast.error("Seul le compte principal peut modifier cette configuration.");
      return;
    }

    if (tag._id) {
      await updateTag(tag._id, { name: tag.name, color: tag.color });
    } else {
      await createTag({ name: tag.name, color: tag.color });
    }
    setShowForm(false);
    setEditingTag(null);
    fetchTags();
  };

  const handleDelete = async (id: string) => {

    if (user?.isParent === false) {
      toast.error("Seul le compte principal peut modifier cette configuration.");
      return;
    }

    await deleteTag(id);
    fetchTags();
  };

  return (
    <div className="">
      {/* Header */}
      <div className="bg-white border rounded-sm">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-gray-900 font-semibold text-2xl mb-2">Mes Tags</h1>
              <p className="text-gray-600 text-sm">Gérez vos étiquettes et leurs couleurs</p>
            </div>
            <button
              className="bg-toola-500 text-white rounded-lg px-4 py-2 hover:bg-opacity-90 transition-colors flex items-center gap-2"
              onClick={() => {
                setEditingTag(null);
                setShowForm(true);
              }}
            >
              <span className="text-lg">+</span>
              Ajouter un Tag
            </button>
          </div>
        </div>
      </div>
  
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {tags.length === 0 ? (
          // État vide
          <div className="bg-white border-gray-100 rounded-lg shadow-sm p-12 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl text-gray-400">#</span>
            </div>
            <h3 className="text-gray-900 font-semibold text-lg mb-2">Aucun tag créé</h3>
            <p className="text-gray-600 text-sm mb-6">Commencez par créer votre premier tag pour organiser vos contenus</p>
            <button
              className="bg-toola-500 text-white rounded-lg px-4 py-2 hover:bg-opacity-90 transition-colors"
              onClick={() => {
                setEditingTag(null);
                setShowForm(true);
              }}
            >
              Créer mon premier tag
            </button>
          </div>
        ) : (
          // Liste des tags
          <div className="bg-white border-gray-100 rounded-lg shadow-sm overflow-hidden">
            {/* En-tête du tableau */}
            <div className="bg-gray-50 px-6 py-4 border-b border-gray-100">
              <div className="grid grid-cols-12 gap-4 items-center">
                <div className="col-span-5">
                  <span className="text-gray-600 text-sm font-medium">Nom du tag</span>
                </div>
                <div className="col-span-4">
                  <span className="text-gray-600 text-sm font-medium">Couleur</span>
                </div>
                <div className="col-span-3 text-right">
                  <span className="text-gray-600 text-sm font-medium">Actions</span>
                </div>
              </div>
            </div>
  
            {/* Lignes du tableau */}
            <div className="divide-y divide-gray-100">
              {tags.map((tag, index) => (
                <div
                  key={tag._id}
                  className={`px-6 py-4 hover:bg-gray-50 transition-colors ${
                    index % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'
                  }`}
                >
                  <div className="grid grid-cols-12 gap-4 items-center">
                    {/* Nom du tag */}
                    <div className="col-span-5">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-4 h-4 rounded-full border-2 border-white shadow-sm"
                          style={{ backgroundColor: tag.color }}
                        ></div>
                        <span className="text-gray-900 font-medium">{tag.name}</span>
                      </div>
                    </div>
  
                    {/* Couleur */}
                    <div className="col-span-4">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-8 h-8 rounded-lg border border-gray-200"
                          style={{ backgroundColor: tag.color }}
                        ></div>
                        <div>
                          <span className="text-gray-900 text-sm font-mono">{tag.color}</span>
                        </div>
                      </div>
                    </div>
  
                    {/* Actions */}
                    <div className="col-span-3 flex items-center justify-end gap-2">
                      <button
                        className="border-toola-500 text-toola-500 border rounded-lg px-3 py-1.5 text-sm hover:bg-toola-500 hover:bg-opacity-5 transition-colors"
                        onClick={() => {
                          setEditingTag(tag);
                          setShowForm(true);
                        }}
                      >
                        Modifier
                      </button>
                      <button
                        className="bg-red-400 text-white rounded-lg px-3 py-1.5 text-sm hover:bg-red-500 transition-colors"
                        onClick={() => handleDelete(tag._id)}
                      >
                        Supprimer
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
  
            {/* Footer avec statistiques */}
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-100">
              <div className="flex items-center justify-between">
                <span className="text-gray-600 text-sm">
                  {tags.length} tag{tags.length > 1 ? 's' : ''} au total
                </span>
                <button
                  className="text-toola-500 text-sm hover:underline transition-colors"
                  onClick={() => {
                    setEditingTag(null);
                    setShowForm(true);
                  }}
                >
                  + Ajouter un autre tag
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
  
      {/* Formulaire (création ou édition) */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full">
            <TagForm
              initialTag={editingTag}
              onSave={handleSave}
              onCancel={() => {
                setShowForm(false);
                setEditingTag(null);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
  
  };

export default TagList;